// Source-level (DOM-free) transformations of the composition HTML for natural text editing.

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtmlText(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Newlines become `<br>` so the raster reflows the same way the editor overlay does.
export function editableValueToHtml(value: string): string {
    return escapeHtmlText(value).replace(/\r\n|\r|\n/g, '<br>');
}

/**
 * Replace the text content of the element with the given `id` with `value`, escaped as plain text.
 * A targeted edit — the open tag and the rest of the document are preserved. Throws on a missing id.
 */
export function replaceEditableText(html: string, id: string, value: string): string {
    const escapedId = escapeRegExp(id);
    const element = new RegExp(
        `(<([a-zA-Z][\\w-]*)\\b[^>]*\\sid\\s*=\\s*(?:"${escapedId}"|'${escapedId}')[^>]*>)[\\s\\S]*?(</\\2>)`,
        'i',
    );
    if (!element.test(html)) {
        throw new Error(`replaceEditableText: no element with id="${id}"`);
    }
    return html.replace(element, (_match, openTag, _tagName, closeTag) => {
        return `${openTag}${editableValueToHtml(value)}${closeTag}`;
    });
}

/**
 * Merge CSS declarations into the inline `style` of the element with the given `id` (an editable
 * node), preserving its other attributes and unrelated style declarations. Keys are CSS property
 * names; an empty-string value removes that declaration. Used to persist in-editor move/resize
 * (element `transform`/`width`/`height`) into `#html`. Throws if no element has the id.
 */
export function applyEditableStyle(
    html: string,
    id: string,
    styles: Record<string, string>,
): string {
    const escapedId = escapeRegExp(id);
    const openTag = new RegExp(
        `<[a-zA-Z][\\w-]*\\b[^>]*\\sid\\s*=\\s*(?:"${escapedId}"|'${escapedId}')[^>]*>`,
        'i',
    );
    const match = openTag.exec(html);
    if (!match) {
        throw new Error(`applyEditableStyle: no element with id="${id}"`);
    }
    const merged = mergeInlineStyle(match[0], styles);
    return html.slice(0, match.index) + merged + html.slice(match.index + match[0].length);
}

function mergeInlineStyle(openTag: string, styles: Record<string, string>): string {
    const styleAttr = /\sstyle\s*=\s*(?:"([^"]*)"|'([^']*)')/i;
    const existing = styleAttr.exec(openTag);
    const declarations = new Map<string, string>();
    if (existing) {
        for (const decl of (existing[1] ?? existing[2] ?? '').split(';')) {
            const colon = decl.indexOf(':');
            if (colon === -1) {
                continue;
            }
            const prop = decl.slice(0, colon).trim();
            if (prop) {
                declarations.set(prop, decl.slice(colon + 1).trim());
            }
        }
    }
    for (const [prop, value] of Object.entries(styles)) {
        if (value === '') {
            declarations.delete(prop);
        } else {
            declarations.set(prop, value);
        }
    }
    const cssText = Array.from(declarations, ([prop, value]) => `${prop}: ${value}`).join('; ');
    const styleAttrStr = cssText ? ` style="${cssText}"` : '';
    if (existing) {
        return openTag.replace(styleAttr, styleAttrStr);
    }
    const insertAt = openTag.endsWith('/>') ? openTag.length - 2 : openTag.length - 1;
    return openTag.slice(0, insertAt) + styleAttrStr + openTag.slice(insertAt);
}
